<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <div class="card">
        <div class="card-header">
          <i class="icon-reload"></i> Auditlog
        </div>
        <div class="card-body">
          <div class="audit-filter-bar">
            <div class="audit-filter-toggles">
              <label class="audit-filter-toggle">
                <input v-model="displayImportant" type="checkbox">
                <span>Important entries</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="displayReason" type="checkbox">
                <span>Missing reasons</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="includeTestOnly" type="checkbox">
                <span>Include Test-only</span>
              </label>
            </div>

            <div class="audit-filter-field audit-filter-staff">
              <label>Filter by Staff</label>
              <Autocomplete v-model="filterbyStaff" />
            </div>

            <div class="audit-filter-field">
              <label>Filter by Reason</label>
              <input v-model="filterbyReason" type="text" class="form-control" placeholder="Contains...">
            </div>

            <div class="audit-filter-field">
              <label>Filter by Entry</label>
              <input v-model="filterbyEntry" type="text" class="form-control" placeholder="Contains...">
            </div>

            <div class="audit-filter-field">
              <label>Filter by Action</label>
              <select id="basicSelect" class="form-control" v-model="filterbyAction">
                <option value="">All</option>
                <template v-for="group in actionGroups">
                  <option :key="group.label" disabled class="audit-action-category">{{ group.label }}</option>
                  <option
                    v-for="action in group.options"
                    :key="group.label + '-' + action.value"
                    :value="action.value">
                    {{ action.label }}
                  </option>
                </template>
              </select>
            </div>
          </div>

          <div class="list-group audit-log-list">
            <span v-for="log in auditlog" :key="log.id" class="list-group-item list-group-item-action audit-log-entry">
              <div class="audit-log-date">
                <small v-b-tooltip.hover.html="getDateTooltip(log.date)">{{ formatDateDisplay(log.date) }}</small>
              </div>

              <div class="d-flex mb-2">
                <div class="align-self-center audit-log-icon">
                  <span class="h5"><i :class="getIcon(log.type)"></i></span>
                </div>
                <div class="align-self-center pl-3">
                  <img :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + log.username" alt="" height="50" width="50" class="rounded-circle">
                </div>

                <div class="d-flex align-self-center flex-column audit-log-content">
                  <div class="align-self-start pl-3 audit-log-meta">
                    <span class="audit-staff-name" :style="{ color: roleColour(log) }" :title="roleName(log)">
                      {{ log.username }}
                    </span>
                    <span v-for="serv in sortedServers(log.servers)" :key="log.id + '-' + serv" :class="'badge-' + getColour(serv)" class="badge text-capitalize">
                      {{ serv }}
                    </span>
                    <mark class="auditReason" v-if="log.reason && log.reason.length > 0">
                      <template v-for="(segment, index) in reasonSegments(log.reason)">
                        <a v-if="segment.url" :key="'url-' + index" href="#" @click.prevent="confirmExternalLink(segment.text)">{{ segment.text }}</a>
                        <span v-else :key="'text-' + index">{{ segment.text }}</span>
                      </template>
                    </mark>
                  </div>
                  <span class="pl-3 audit-log-message">{{ log.message }}</span>
                  <small class="pl-3 text-muted">{{ actionLabel(log.type) }}</small>
                </div>
              </div>
            </span>
          </div>

          <br>
          <b-pagination size="md" :total-rows="count" v-model="page" v-on:change="fetchData" :per-page="100"></b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Permissions from '../../_permissions'
import {mapGetters, mapActions} from 'vuex'
import Servers from '../../_servers'
import Roles from '../../_roles'
import Autocomplete from './Permissions/UsernameSearch.vue'
import { buildAuditLogActionGroups } from '../../auditlog_permissions'

const urlPattern = /(https?:\/\/[^\s<>"']+)/gi

export default {
  name: 'Auditlog',
  computed: {
    ...mapGetters({
      auditlog: 'Auditlog',
      count: 'LogCount'
    }),
    actionGroups () {
      return buildAuditLogActionGroups(permission => this.hasPermission(permission))
    }
  },
  data () {
    return {
      actionLookupGroups: buildAuditLogActionGroups(),
      page: 1,
      filterbyStaff: null,
      filterbyReason: '',
      filterbyEntry: '',
      displayImportant: true,
      displayReason: false,
      includeTestOnly: false,
      filterbyAction: '',
      permissions: Permissions
    }
  },
  created () {
    this.clearAuditlog()
  },
  components: {
    Autocomplete
  },
  mounted () {
    this.fetchLog(true)
  },
  watch: {
    displayImportant () {
      this.updateLog()
    },
    displayReason () {
      this.updateLog()
    },
    includeTestOnly () {
      this.updateLog()
    },
    filterbyAction () {
      this.updateLog()
    },
    filterbyStaff () {
      this.updateLog()
    },
    filterbyReason () {
      this.updateLog()
    },
    filterbyEntry () {
      this.updateLog()
    }
  },
  methods: {
    ...mapActions(['clearAuditlog']),
    updateLog () {
      this.page = 1
      this.fetchLog(true)
    },
    fetchData (e) {
      this.page = e
      this.fetchLog(false)
    },
    fetchLog (count) {
      this.clearAuditlog()
      const staff = this.filterbyStaff && this.filterbyStaff.name ? this.filterbyStaff.name : ''
      this.emit('getAuditlog', {
        page: this.page,
        count: count,
        display_only_important: this.displayImportant,
        display_only_reasons: this.displayReason,
        include_test_only: this.includeTestOnly,
        filterbyAction: this.filterbyAction,
        filter_by_user: staff,
        filter_by_staff: staff,
        filter_by_reason: this.filterbyReason.trim(),
        filter_by_entry: this.filterbyEntry.trim()
      })
    },
    sortedServers (servers) {
      if (!Array.isArray(servers)) {
        return []
      }
      return servers.slice().sort()
    },
    getColour (server) {
      if (server in Servers) {
        return Servers[server].colour
      }
      return server
    },
    roleColour (log) {
      const role = Roles[log.staff_security_id]
      return role ? role.colour : '#d8dee4'
    },
    roleName (log) {
      const role = Roles[log.staff_security_id]
      return log.staff_security_name || (role ? role.name : 'Player')
    },
    actionLabel (type) {
      for (const group of this.actionLookupGroups) {
        const action = group.options.find(item => item.value === type)
        if (action) {
          return action.label
        }
      }
      return type
    },
    getIcon (type) {
      for (let i in this.permissions) {
        for (let i2 in this.permissions[i].Permissions) {
          if (type === i2 && 'icon' in this.permissions[i].Permissions[i2]) {
            return this.permissions[i].Permissions[i2].icon
          }
        }
      }
      for (const group of this.actionLookupGroups) {
        const action = group.options.find(item => item.value === type)
        if (action) {
          return action.icon
        }
      }
      return 'fa fa-pencil'
    },
    reasonSegments (reason) {
      if (!reason) {
        return []
      }
      const parts = []
      let lastIndex = 0
      reason.replace(urlPattern, (match, url, offset) => {
        if (offset > lastIndex) {
          parts.push({text: reason.slice(lastIndex, offset), url: false})
        }
        parts.push({text: match, url: true})
        lastIndex = offset + match.length
        return match
      })
      if (lastIndex < reason.length) {
        parts.push({text: reason.slice(lastIndex), url: false})
      }
      return parts
    },
    async confirmExternalLink (url) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This link opens a page outside the tools and may be unsafe.',
        {
          title: 'Open external link?',
          okTitle: 'Open Link',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        window.open(url, '_blank', 'noopener')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-filter-bar {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(220px, 1.2fr);
  gap: 14px;
  align-items: end;
  padding: 14px;
  margin-bottom: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-filter-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.audit-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.16);
  font-size: 0.82rem;
}

.audit-filter-toggle input {
  margin: 0;
}

.audit-filter-field label {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-log-list {
  gap: 10px;
}

.audit-log-entry {
  position: relative;
  background: rgba(255, 255, 255, 0.025);
  border-color: rgba(255, 255, 255, 0.08);
}

.audit-log-date {
  position: absolute;
  top: 12px;
  right: 14px;
  color: rgba(255, 255, 255, 0.62);
}

.audit-log-icon {
  width: 24px;
}

.audit-log-content {
  min-width: 0;
  padding-right: 160px;
}

.audit-log-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.audit-staff-name {
  font-weight: 700;
}

.audit-log-message,
.auditReason {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.auditReason a {
  color: #59dcc2;
  text-decoration: underline;
}

.audit-action-category {
  color: #8f9ba6;
  background: #1b2229;
}

@media (max-width: 1199px) {
  .audit-filter-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .audit-filter-bar {
    grid-template-columns: 1fr;
  }

  .audit-log-date {
    position: static;
    margin-bottom: 8px;
    text-align: right;
  }

  .audit-log-content {
    padding-right: 0;
  }
}
</style>
